import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, CircleAlert, Lock, Undo2 } from "lucide-react";
import { Button, cn } from "@nexus/ui";
import { isBrandBrainNexusColumn, type BrandBrainNexusColumn, type BrandBrainSectionId, type OnboardingEditColumn } from "@nexus/contracts";
import { formatBrandBrainValue, type BrandBrainRow, type BrandBrainSectionView } from "@/features/brand-brain/lib/brand-brain-view";
import { brandBrainFieldHint, brandBrainFieldLabel, brandBrainSectionLabel } from "@/features/brand-brain/lib/brand-brain-labels";
import type { BrandBrainDraft, BrandBrainFieldErrors } from "@/features/brand-brain/lib/brand-brain-draft";
import {
  ClientOnboardingField,
  isShortOnboardingField,
  onboardingFieldErrorMessage,
  onboardingFieldErrorText,
} from "@/features/identity-hub/components/dashboard/onboarding/ClientOnboardingFields";
import type { OnboardingDraftValue, OnboardingFieldErrors } from "@/features/identity-hub/lib/client-onboarding";
import type { OnboardingSaveError } from "@/features/identity-hub/lib/onboarding-save-error";

export const MONO_LABEL = "font-mono text-[10px] tracking-[0.06em] text-muted-foreground uppercase";

/** 44px touch target, compact on fine pointers, with the design-system Button focus ring. */
export const INTERACTIVE_TARGET_CLASS =
  "min-h-11 min-w-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8 [@media(hover:hover)_and_(pointer:fine)]:min-w-8 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40";

export const NOT_COLLECTED_CHIP =
  "inline-flex h-[21px] items-center gap-1.5 rounded-full border border-dashed border-input-border px-[9px] text-[11px] text-muted-foreground";

/** Readable line length for long values, the same measure as other Nexus reading text. */
const READING_WIDTH_CLASS = "max-w-[68ch]";

const SECTION_TAGS: Partial<Record<BrandBrainSectionId, { label: string; className: string }>> = {
  "messaging-guidelines": { label: "Default vendor context", className: "bg-primary/5 text-primary" },
  "enriched-brand-knowledge": { label: "Generated report", className: "bg-ai-fill-chip text-ai-text" },
};

/** The one section being edited: its draft, what changed and how to save it. */
export interface BrandBrainSectionEditor {
  draft: BrandBrainDraft;
  errors: BrandBrainFieldErrors;
  saveError: OnboardingSaveError | null;
  /** Banner text for a rejection that names no field in this section. */
  saveErrorBanner: string | null;
  changedColumns: readonly string[];
  canSave: boolean;
  isSaving: boolean;
  onChange: (column: string, value: OnboardingDraftValue) => void;
  onUndo: (column: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

interface BrandBrainSectionCardProps {
  section: BrandBrainSectionView;
  /** Set only on the section being edited. */
  editor: BrandBrainSectionEditor | null;
  /** The caller may edit this section now (permission, writes on, a record exists, editable fields). */
  canEdit: boolean;
  /** Opens this section for editing; `column` puts the cursor in that field. */
  onEdit: (column?: string) => void;
}

/**
 * One Brand Brain section. Read mode lists every field; gaps the caller can fill get "Add". Edit mode
 * turns only this section's editable fields into inputs and folds the read-only ones underneath.
 */
export function BrandBrainSectionCard({ section, editor, canEdit, onEdit }: BrandBrainSectionCardProps) {
  const { t } = useTranslation();
  const tag = SECTION_TAGS[section.id];
  const editableRows = section.rows.filter((row) => row.editable);
  const readOnlyRows = section.rows.filter((row) => !row.editable);
  const isEditable = canEdit && editableRows.length > 0;

  return (
    <section
      id={`brand-brain-${section.id}`}
      className={cn("scroll-mt-4 rounded-md border bg-card", editor && "border-primary/40 ring-1 ring-primary/20")}
      aria-labelledby={`brand-brain-heading-${section.id}`}
      data-testid={`brand-brain-section-${section.id}`}
    >
      <div className="flex flex-wrap items-center gap-2.5 rounded-t-md border-b bg-muted px-3.5 py-2.5">
        <h2
          id={`brand-brain-heading-${section.id}`}
          className="flex items-center gap-2.5 text-[13px] font-semibold text-foreground"
        >
          <span className="grid size-[22px] flex-none place-items-center rounded-[5px] bg-secondary-subtle text-[11px] font-normal tabular-nums text-secondary-subtle-foreground">
            {section.number}
          </span>
          {brandBrainSectionLabel(t, section.id)}
        </h2>
        {tag ? (
          <span className={cn("inline-flex h-[19px] items-center rounded-full px-2 text-[10.5px] font-medium", tag.className)}>
            {t(`brandBrain.tags.${section.id}`, tag.label)}
          </span>
        ) : null}
        {editor ? (
          <span className="inline-flex h-[19px] items-center rounded-full bg-primary/10 px-2 text-[10.5px] font-medium text-primary">
            {t("brandBrain.edit.editingTag", "Editing")}
          </span>
        ) : null}
        <p className="ml-auto flex-none text-[11px] text-muted-foreground">
          {t("brandBrain.fieldCount", {
            count: section.fieldCount,
            defaultValue_one: "{{count}} field",
            defaultValue_other: "{{count}} fields",
          })}
          {" · "}
          {section.gapCount === 0
            ? t("brandBrain.complete", "complete")
            : t("brandBrain.notCollectedCount", {
                count: section.gapCount,
                defaultValue_one: "{{count}} not collected",
                defaultValue_other: "{{count}} not collected",
              })}
        </p>
        {isEditable && !editor ? (
          <Button
            size="sm"
            variant="outline"
            iconStart="pencil"
            onClick={() => onEdit()}
            data-testid={`brand-brain-edit-${section.id}`}
          >
            {t("brandBrain.edit.editSection", "Edit section")}
          </Button>
        ) : null}
      </div>

      {editor ? (
        <SectionEditor editableRows={editableRows} readOnlyRows={readOnlyRows} editor={editor} />
      ) : section.rows.length > 0 ? (
        <ul>
          {section.rows.map((row) => (
            <ReadRow key={row.id} row={row} onAdd={canEdit && row.editable && row.isGap ? () => onEdit(row.column) : null} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function SectionEditor({
  editableRows,
  readOnlyRows,
  editor,
}: {
  editableRows: readonly BrandBrainRow[];
  readOnlyRows: readonly BrandBrainRow[];
  editor: BrandBrainSectionEditor;
}) {
  const { t } = useTranslation();
  const errorCount = editableRows.filter(
    (row) => onboardingFieldErrorText(t, row.column as OnboardingEditColumn, editor.errors, editor.saveError) !== undefined,
  ).length;
  const changedCount = editor.changedColumns.length;

  return (
    <>
      <div className="@container px-3.5 py-4">
        <ul className="grid gap-x-6 gap-y-5 @md:grid-cols-2">
          {editableRows.map((row) => (
            <EditRow key={row.id} row={row} editor={editor} />
          ))}
        </ul>
      </div>
      {readOnlyRows.length > 0 ? <ReadOnlyGroup rows={readOnlyRows} /> : null}
      {editor.saveErrorBanner ? (
        <p
          className="mx-3.5 mt-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-foreground"
          role="alert"
          data-testid="brand-brain-save-error"
        >
          {editor.saveErrorBanner}
        </p>
      ) : null}
      {/* Sticks to the bottom of the page while this section is on screen, so Save never scrolls away. */}
      <div
        className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 rounded-b-md border-t bg-card/95 px-3.5 py-2.5 backdrop-blur-sm"
        data-testid="brand-brain-edit-bar"
      >
        <p className="text-[11.5px] text-muted-foreground" aria-live="polite">
          {changedCount === 0
            ? t("brandBrain.edit.noChanges", "No changes yet")
            : t("brandBrain.edit.unsavedCount", {
                count: changedCount,
                defaultValue_one: "{{count}} unsaved change",
                defaultValue_other: "{{count}} unsaved changes",
              })}
          {errorCount > 0 ? (
            <span className="text-destructive">
              {" · "}
              {t("brandBrain.edit.errorCount", {
                count: errorCount,
                defaultValue_one: "{{count}} field to fix",
                defaultValue_other: "{{count}} fields to fix",
              })}
            </span>
          ) : null}
        </p>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="ghost" disabled={editor.isSaving} onClick={editor.onCancel} data-testid="brand-brain-cancel">
            {t("brandBrain.edit.cancel", "Cancel")}
          </Button>
          <Button size="sm" iconStart="check" disabled={!editor.canSave} onClick={editor.onSave} data-testid="brand-brain-save">
            {editor.isSaving
              ? t("brandBrain.edit.saving", "Saving…")
              : changedCount > 0
                ? t("brandBrain.edit.saveCount", {
                    count: changedCount,
                    defaultValue_one: "Save {{count}} change",
                    defaultValue_other: "Save {{count}} changes",
                  })
                : t("brandBrain.edit.save", "Save changes")}
          </Button>
        </div>
      </div>
    </>
  );
}

function EditRow({ row, editor }: { row: BrandBrainRow; editor: BrandBrainSectionEditor }) {
  const { t } = useTranslation();
  const column = row.column;
  const label = brandBrainFieldLabel(t, row.column);
  const isChanged = editor.changedColumns.includes(row.column);
  const isNexus = isBrandBrainNexusColumn(column);

  return (
    <li
      className={cn(
        // The accent border is always there (transparent until changed) so changed fields stay aligned.
        "flex min-w-0 flex-col gap-1.5 border-l-2 border-transparent pl-3",
        (isNexus || !isShortOnboardingField(column as OnboardingEditColumn)) && "col-span-full",
        isChanged && "border-primary",
      )}
      data-testid={`brand-brain-field-${row.column}`}
    >
      {isNexus ? (
        <NexusTextField column={column} label={label} editor={editor} />
      ) : (
        <ClientOnboardingField
          column={column as OnboardingEditColumn}
          label={label}
          value={editor.draft[column as OnboardingEditColumn]}
          errorMessage={onboardingFieldErrorText(t, column as OnboardingEditColumn, editor.errors as OnboardingFieldErrors, editor.saveError)}
          onChange={(value) => editor.onChange(column, value)}
          disabled={editor.isSaving}
        />
      )}
      {isChanged ? (
        <div className="flex items-center gap-2 text-[11px]">
          <span className="font-medium text-primary">{t("brandBrain.edit.changed", "Changed")}</span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
            onClick={() => editor.onUndo(column)}
            disabled={editor.isSaving}
            aria-label={t("brandBrain.edit.undoField", { field: label, defaultValue: "Undo changes to {{field}}" })}
            data-testid={`brand-brain-undo-${row.column}`}
          >
            <Undo2 className="size-3" aria-hidden />
            {t("brandBrain.edit.undo", "Undo")}
          </button>
        </div>
      ) : null}
    </li>
  );
}

/** A Nexus-owned field: free text, kept exactly as typed, at the same measure and look as the onboarding fields. */
function NexusTextField({ column, label, editor }: { column: BrandBrainNexusColumn; label: string; editor: BrandBrainSectionEditor }) {
  const { t } = useTranslation();
  const inputId = useId();
  const hintId = useId();
  const errorId = useId();
  const fieldError = editor.errors[column];
  const error = fieldError
    ? onboardingFieldErrorMessage(t, fieldError)
    : editor.saveError?.field === column
      ? editor.saveError.message
      : undefined;
  const hint = brandBrainFieldHint(t, column);
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("flex w-full min-w-0 flex-col gap-1.5", READING_WIDTH_CLASS)}>
      <label htmlFor={inputId} className="text-xs font-medium text-foreground">
        {label}
      </label>
      {hint ? (
        <p id={hintId} className="-mt-0.5 text-[11px] leading-snug text-muted-foreground">
          {hint}
        </p>
      ) : null}
      <textarea
        id={inputId}
        value={editor.draft[column] ?? ""}
        onChange={(e) => editor.onChange(column, e.target.value)}
        disabled={editor.isSaving}
        rows={4}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          "w-full resize-y rounded-md border bg-background px-3 py-2 text-[13px] leading-relaxed text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50",
          "min-h-24 max-h-96 field-sizing-content",
          error ? "border-destructive focus:border-destructive" : "border-input-border",
        )}
      />
      {error ? (
        <p id={errorId} className="text-[11px] text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Generated and create-only fields stay one click away while the section is edited, never as inputs. */
function ReadOnlyGroup({ rows }: { rows: readonly BrandBrainRow[] }) {
  const { t } = useTranslation();
  const contentId = useId();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-t border-border-subtle" data-testid="brand-brain-read-only-group">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={contentId}
        onClick={() => setIsOpen((open) => !open)}
        className={cn(INTERACTIVE_TARGET_CLASS, "flex w-full items-center gap-2 px-3.5 py-2 text-left text-xs text-muted-foreground hover:bg-muted")}
      >
        <ChevronRight className={cn("size-3.5 transition-transform", isOpen && "rotate-90")} aria-hidden />
        <Lock className="size-3" aria-hidden />
        {t("brandBrain.edit.readOnlyGroup", {
          count: rows.length,
          defaultValue_one: "{{count}} read-only field",
          defaultValue_other: "{{count}} read-only fields",
        })}
        <span className="text-[11px]">{t("brandBrain.edit.readOnlyHint", "· generated by Adge or set at onboarding")}</span>
      </button>
      <ul id={contentId} hidden={!isOpen}>
        {rows.map((row) => (
          <ReadRow key={row.id} row={row} onAdd={null} />
        ))}
      </ul>
    </div>
  );
}

function ReadRow({ row, onAdd }: { row: BrandBrainRow; onAdd: (() => void) | null }) {
  const { t } = useTranslation();
  const label = brandBrainFieldLabel(t, row.column);

  return (
    <li className="grid items-baseline gap-1 border-b border-border-subtle px-3.5 py-[9px] last:border-b-0 hover:bg-muted sm:grid-cols-[210px_minmax(0,1fr)_auto] sm:gap-3.5">
      <span className="text-[12.5px] text-secondary-subtle-foreground">{label}</span>
      {/* pre-wrap keeps saved line breaks and spacing, so claims and disclaimers read exactly as written. */}
      <span
        className={cn("min-w-0 text-[12.5px] leading-[1.55] break-words whitespace-pre-wrap text-foreground", READING_WIDTH_CLASS)}
        data-testid={`brand-brain-value-${row.column}`}
      >
        {row.isGap ? (
          <span className={NOT_COLLECTED_CHIP}>
            <CircleAlert className="size-[11px] flex-none" strokeWidth={1.5} aria-hidden="true" />
            {t("brandBrain.notCollected", "Not collected")}
          </span>
        ) : (
          formatBrandBrainValue(row.value)
        )}
      </span>
      {onAdd ? (
        <Button
          size="sm"
          variant="ghost"
          iconStart="plus"
          className="justify-self-start text-primary sm:justify-self-end"
          onClick={onAdd}
          aria-label={t("brandBrain.edit.addField", { field: label, defaultValue: "Add {{field}}" })}
          data-testid={`brand-brain-add-${row.column}`}
        >
          {t("brandBrain.edit.add", "Add")}
        </Button>
      ) : (
        <span aria-hidden />
      )}
    </li>
  );
}
